import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apis from '../../libs/services'
import { useAuth } from '../../hooks/useAuth'
import { PageTitle } from '../../components/atoms'
import { getPostLoginDestination } from '../../utils/deeplink'
import { isMobileDevice } from '../../components/molecules/MobileDetectionWrapper/MobileDetectionWrapper'
import { PhoneBlockingDialog } from '../../components/molecules/PhoneBlockingDialog/PhoneBlockingDialog'

function SAML2Callback (): ReactElement {
  const [status, setStatus] = useState<'processing' | 'success' | 'error' | 'phone_blocked'>('processing')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const navigate = useNavigate()
  const { setUserProfile } = useAuth()
  // DMVP-1080: setUserProfile is a new function every AuthProvider render, so this
  // effect would otherwise re-run and flood sendPhoneLoginReminder.
  const hasHandledRef = useRef(false)

  useEffect(() => {
    const handleSAMLResponse = async (): Promise<void> => {
      if (hasHandledRef.current) {
        return
      }

      try {
        // Get token from URL hash fragment
        const hash = window.location.hash.substring(1) // Remove the leading '#'
        const hashParams = new URLSearchParams(hash)
        const authToken = hashParams.get('token')

        if (authToken === null) {
          throw new Error('No authToken response received')
        }

        hasHandledRef.current = true

        // Store token
        localStorage.setItem('dopageAccessToken', authToken)

        // Fetch user profile
        const profileRes = await apis.getUserProfile()
        const profileData = profileRes?.data?.data
        setUserProfile(profileData)

        // Phone interception: block on mobile, except when a mobile-allowed survey is pending.
        if (isMobileDevice() && profileData?.pending_survey !== true) {
          try {
            await apis.sendPhoneLoginReminder(profileData.email)
          } catch {
            // Best-effort — still show blocking dialog even if email fails
          }
          setStatus('phone_blocked')
          return
        }

        setStatus('success')

        // Navigate based on role
        if (profileData.user_profile.accepted_terms === true) {
          if (profileData.role.slug === 'admin') {
            navigate('/dashboard/all-assigned-students')
          } else {
            navigate(getPostLoginDestination())
          }
        } else {
          navigate('/terms-of-use')
        }
      } catch (error) {
        console.error('SAML authentication error:', error)
        setStatus('error')
        setErrorMessage('Authentication failed. Please try again.')
      }
    }

    void handleSAMLResponse()
  }, [navigate, setUserProfile])

  // Phone blocked state — user authenticated on phone, show blocking dialog
  if (status === 'phone_blocked') {
    return <PhoneBlockingDialog />
  }

  // Render loading, success, or error state
  return (
    <div className='bg-black min-h-[100dvh] font-primary flex flex-col items-center justify-center'>
      <PageTitle title='SAML Authentication' />
      {status === 'processing' && (
        <div className='text-white text-[26px]'>Authenticating...</div>
      )}
      {status === 'error' && (
        <div className='text-red-500 text-[26px]'>Error: {errorMessage}</div>
      )}
    </div>
  )
}

export default SAML2Callback

